<template>
  <div id="site-container" class="site-container" :style="containerStyles">
    <CommunityStandardsOverlay v-if="isSpam" />
    <SensitiveMaterialOverlay
      v-else-if="isSensitiveOverlayEnabled && !hasAcknowledgedOverlay"
      :age="isFreePlan ? null : ageLimit"
      :hide-branding="campsite.site_hide_branding"
      @continue="hasAcknowledgedOverlay = true"
    />
    <template v-else>
      <div class="site-bg" v-if="isEnabled" />
      <main class="site__main">
        <client-only>
          <div
            class="site__share-container"
            v-if="
              !isLargeScreen &&
              !isIframe &&
              showHeader &&
              isEnabled &&
              !hideShareButton
            "
          >
            <ShareButton class="site__share" />
          </div>
        </client-only>
        <Header
          :is-enabled="isEnabled"
          :show="showHeader"
          :hide-share-button="hideShareButton"
        >
          <SocialAccounts
            isTop
            v-if="
              socialAccounts.length &&
              isEnabled &&
              campsite.site_social_position === 'top'
            "
            :socialAccounts="socialAccounts"
            :campsite="campsite"
            :isFreePlan="isFreePlan"
          />
        </Header>

        <template v-if="isEnabled">
          <Links :isFreePlan="isFreePlan" />
        </template>
        <template v-else>
          <DummyLinks />
        </template>
        <SocialAccounts
          v-if="
            socialAccounts.length &&
            isEnabled &&
            (!campsite.site_social_position ||
              campsite.site_social_position === 'bottom')
          "
          :socialAccounts="socialAccounts"
          :campsite="campsite"
          :isFreePlan="isFreePlan"
        />
        <Footer :is-enabled="isEnabled" />
      </main>
    </template>
  </div>
</template>

<script>
import {
  PROFILE_BG_GRADIENTS,
  LoadingBlock,
  getBackgroundImageUrlsFromMediaUrl,
} from '@campsite-bio/component-lib';
import throttle from 'lodash/throttle';
import { computed, ref, useContext } from '@nuxtjs/composition-api';

import Header from '@/components/header';
import Footer from '@/components/footer';
import Links from '@/components/links';
import SocialAccounts from '@/components/social-accounts';
import DummyLinks from '@/components/dummy-links';
import ShareButton from '@/components/share-button';
import { endOfPage } from '@/components/end-of-page';
import { head } from '@/components/head';
import { loadScript } from '@/utils/load-script';
import { getMeta } from '@/utils/meta';
import { useMediaQuery } from '@vueuse/core';

export default {
  components: {
    Header,
    Footer,
    Links,
    DummyLinks,
    SocialAccounts,
    ShareButton,
    SensitiveMaterialOverlay: () => ({
      component: import('@/components/sensitive-material-overlay').then(
        (d) => d.default,
      ),
      loading: LoadingBlock,
    }),
    CommunityStandardsOverlay: () => ({
      component: import('@/components/community-standards-overlay').then(
        (d) => d.default,
      ),
      loading: LoadingBlock,
    }),
  },

  head,

  async asyncData({ params, store, error, $sentry }) {
    let username = params.pathMatch.replace('/', '');

    try {
      await store.dispatch('get', {
        username,
      });

      return {
        username,
      };
    } catch (e) {
      console.error(e);

      if (e.isAxiosError) {
        error({
          statusCode: 404,
          message: 'Profile not found',
        });
      } else {
        $sentry.captureException(e);
        error({
          statusCode: 500,
          message: 'There was an error getting this profile',
        });
      }
    }
  },

  setup() {
    const hasAcknowledgedOverlay = ref(false);
    const { store } = useContext();

    const isLargeScreen = useMediaQuery('(min-width: 992px)');

    const campsite = computed(() => store.state.campsite);
    const isSensitiveOverlayEnabled = computed(() => {
      return getMeta('enable_sensitive_content', false, campsite.value.meta);
    });

    const ageLimit = computed(() => {
      return getMeta('sensitive_content_age_limit', null, campsite.value.meta);
    });

    const showHeader = computed(() => {
      return (
        !campsite.value.site_hide_profile_image ||
        !campsite.value.site_hide_intro_title ||
        !!campsite.value.site_location ||
        !!campsite.value.site_intro_text
      );
    });

    const isFreePlan = computed(() => {
      return store.getters.isFreePlan;
    });

    const hideShareButton = computed(() => {
      if (isFreePlan.value) return false;

      return campsite.value.site_hide_share;
    });

    return {
      isSensitiveOverlayEnabled,
      hasAcknowledgedOverlay,
      ageLimit,
      showHeader,
      isLargeScreen,
      hideShareButton,
    };
  },

  data() {
    return {
      isLoadingLinks: false,
      username: '',
    };
  },

  computed: {
    user() {
      return this.$store.state.user;
    },

    campsite() {
      return this.$store.state.campsite;
    },

    socialAccounts() {
      return this.$store.state.socialAccounts;
    },

    isEnabled() {
      return this.campsite.enabled;
    },

    isSpam() {
      return this.user.spam;
    },

    isIframe() {
      return this.$route.query.iframe === '1';
    },

    isSubdomain() {
      if (!process.client) return false;

      const hostname = location.hostname;
      const subdomain = hostname.split('.')[0];

      return (
        this.campsite.subdomain &&
        hostname.includes('campsite.bio') &&
        subdomain !== 'campsite'
      );
    },

    isCustomDomain() {
      return (
        process.client &&
        this.campsite.custom_domain &&
        !window.location.hostname.includes('campsite.bio')
      );
    },

    bodyClasses() {
      const { site_bg_style, site_bg_animated } = this.campsite.theme
        ? this.campsite.theme
        : this.campsite;
      const classes = ['site'];
      if (this.isSpam) classes.push('site--is-spam');
      if (this.campsite.site_hide_branding) classes.push('site--hide-branding');
      if (site_bg_style === 'gradient') {
        classes.push('site--has-gradient');
        if (site_bg_animated) classes.push('site--has-animated-gradient');
      } else if (site_bg_style === 'image') classes.push('site--has-bg-image');
      else if (site_bg_style === 'pattern')
        classes.push('site--has-bg-pattern');
      if (!this.isEnabled) classes.push('site--coming-soon');
      if (
        this.socialAccounts.length &&
        !this.isSpam &&
        (!this.campsite.site_social_position ||
          this.campsite.site_social_position === 'bottom')
      )
        classes.push('site--has-social');

      if (process.client && this.isIframe) {
        classes.push('site--iframe');
        if (this.campsite.site_embed_disable_bg)
          classes.push('site--iframe-no-bg');
      }
      return classes;
    },

    containerStyles() {
      let styles = {};
      const settings = this.campsite.theme
        ? this.campsite.theme
        : this.campsite;

      if (
        this.campsite.site_bg_style === 'image' &&
        this.campsite.site_bg_image &&
        this.campsite.site_bg_image.mobile
      ) {
        let { mobile, tablet, desktop } = this.campsite.site_bg_image;
        // Fix issue with older custom image backgrounds that aren't encoded
        if (
          mobile.includes(this.$config.cdnHost) &&
          (mobile.includes('.png') ||
            mobile.includes('.jpg') ||
            mobile.includes('.jpeg'))
        ) {
          const bgImages = getBackgroundImageUrlsFromMediaUrl(mobile);
          mobile = bgImages.mobile;
          tablet = bgImages.tablet;
          desktop = bgImages.desktop;
        }
        let gradient;
        if (this.campsite.site_bg_image_filter) {
          gradient =
            PROFILE_BG_GRADIENTS[
              this.campsite.site_bg_image_filter.toUpperCase()
            ];
        }

        styles = {
          '--bg-mobile-src': `${
            gradient ? `${gradient}, ` : ''
          }url('${mobile}')`,
          '--bg-tablet-src': `${
            gradient ? `${gradient}, ` : ''
          }url('${tablet}')`,
          '--bg-desktop-src': `${
            gradient ? `${gradient}, ` : ''
          }url('${desktop}')`,
        };
      }

      if (settings.site_bg_style === 'gradient') {
        styles = {
          '--p-bg-gradient': `linear-gradient(${
            settings.site_bg_gradient_direction
          }, ${
            settings.site_bg_colors?.join(',') ||
            `${settings.site_bg_color}, ${settings.site_bg_gradient_color}`
          })`,
        };
      }

      return {
        ...styles,
      };
    },

    isFreePlan() {
      return this.$store.getters.isFreePlan;
    },

    hasCustomFavicon() {
      return !this.isFreePlan && this.campsite.custom_meta_favico;
    },

    hasCustomOGImage() {
      return !this.isFreePlan && this.campsite.custom_meta_ogimage;
    },

    page() {
      return this.$store.state.page;
    },

    hasMoreLinks() {
      return this.$store.state.hasMoreLinks;
    },

    customCode() {
      return this.campsite.custom_code;
    },

    includeCustomCode() {
      return !this.isSpam && this.isCustomDomain && this.customCode;
    },
  },

  created() {
    if (process.client) {
      // Redirect custom domains and subdomains if user is free
      if (this.isFreePlan && (this.isSubdomain || this.isCustomDomain))
        window.location = 'https://campsite.bio';

      this.iframeMessage = this.iframeMessage.bind(this);
      this.scroll = throttle(this.scroll.bind(this), 100);

      if (this.isIframe) {
        loadScript(
          `${this.$config.cdnHost}/js/iframe-resizer@4.2.9/iframeResizer.contentWindow.min.js`,
        );

        // add iframe classes
        document.body.classList.add('site--iframe');
        if (this.campsite.site_embed_disable_bg)
          document.body.classList.add('site--iframe-no-bg');
      }
    }
  },

  mounted() {
    if (process.client) {
      this.addScrollListener();
      window.addEventListener('message', this.iframeMessage, false);

      if (this.includeCustomCode) {
        const range = document.createRange();
        range.setStart(this.$el, 0);
        this.$el.appendChild(range.createContextualFragment(this.customCode));
      }
    }
  },

  beforeDestroy() {
    if (process.client) {
      this.removeScrollListener();
      window.removeEventListener('message', this.iframeMessage, false);
    }
  },

  methods: {
    addScrollListener() {
      window.addEventListener('scroll', this.scroll, { passive: true });
    },

    scroll() {
      if (!this.isLoadingLinks && this.hasMoreLinks && endOfPage(400)) {
        this.$store.commit('set', {
          key: 'page',
          value: this.page + 1,
        });
        this.loadMoreLinks();
      }
    },

    removeScrollListener() {
      window.removeEventListener('scroll', scroll, { passive: true });
    },

    iframeMessage(message) {
      if (message.data === 'scrollBottomCampsite') {
        if (!this.isLoadingLinks && this.hasMoreLinks) {
          this.$store.commit('set', {
            key: 'page',
            value: this.page + 1,
          });
          this.loadMoreLinks();
        }
      }
    },

    async loadMoreLinks() {
      if (!this.isLoadingLinks && this.hasMoreLinks) {
        this.isLoadingLinks = true;
        try {
          await this.$store.dispatch('links', {
            username: this.username,
            page: this.page,
          });
        } catch (e) {
          this.$sentry.captureException(e);
          console.log(e);
          this.$store.commit('set', {
            key: 'page',
            value: this.page - 1,
          });
        }
        this.isLoadingLinks = false;
      }
    },
  },
};
</script>

<style lang="scss"></style>
